// ═══════════════════════════════════════════════════════════════════════════════
// Prescriptive Alerts Engine — Zero backend, deterministic rules
// ═══════════════════════════════════════════════════════════════════════════════

import type { IFAlerta, IFContratoResumo } from '../types/financiamento.types';

export interface Prescricao {
  acao: string;
  prazo: string;
  impactoMin: number;
  impactoMax: number;
  confianca: 'alta' | 'media' | 'baixa';
  severidade: 'critico' | 'atencao' | 'info';
  justificativas: string[];
  consequencia: string;
}

/** Risk factor multiplier */
function riskFactor(scoreRisco: number | null): number {
  const s = scoreRisco ?? 0;
  if (s >= 60) return 1.0;
  if (s >= 40) return 0.8;
  return 0.6;
}

/**
 * Maps an alert to a concrete prescription with action, deadline, impact range,
 * justification, and consequence of inaction.
 */
export function getPrescricao(
  alerta: IFAlerta,
  contratoResumo: IFContratoResumo | undefined,
): Prescricao {
  const valorGlobal = contratoResumo?.valor_global ?? 0;
  const probAtraso = contratoResumo?.prob_atraso ?? 0;
  const scoreRisco = contratoResumo?.score_risco ?? 0;
  const rf = riskFactor(scoreRisco);
  const ctx = alerta.dados_contexto || {};

  // Count available data signals for confidence
  let signals = 0;
  if (contratoResumo?.prob_atraso != null) signals++;
  if (contratoResumo?.score_risco != null) signals++;
  if (contratoResumo?.ultimo_boletim_periodo_fim != null) signals++;
  if (contratoResumo?.tendencia != null) signals++;

  switch (alerta.tipo) {
    case 'risco_elevado': {
      const baseImpact = valorGlobal * probAtraso;
      const impactoMin = baseImpact * Math.max(0.6, rf - 0.2);
      const impactoMax = baseImpact * Math.min(1.0, rf + 0.2);

      const justificativas: string[] = [];
      if (probAtraso > 0.5) justificativas.push(`Probabilidade de atraso: ${(probAtraso * 100).toFixed(0)}%`);
      if (contratoResumo?.tendencia === 'piorando') justificativas.push('Tendência negativa nas últimas medições');
      if (scoreRisco >= 60) justificativas.push(`Score de risco operacional: ${scoreRisco.toFixed(0)}/100`);
      const days = contratoResumo?.ultimo_boletim_periodo_fim
        ? Math.floor((Date.now() - new Date(contratoResumo.ultimo_boletim_periodo_fim).getTime()) / 86_400_000)
        : null;
      if (days != null && days > 30) justificativas.push(`${days} dias sem atualização`);
      if (justificativas.length === 0) justificativas.push('Múltiplos sinais de risco detectados');

      return {
        acao: 'Acionar cláusula de step-in ou exigir plano de recuperação formal da contratada',
        prazo: 'Imediato (≤ 5 dias)',
        impactoMin,
        impactoMax,
        confianca: signals >= 3 ? 'alta' : signals >= 2 ? 'media' : 'baixa',
        severidade: 'critico',
        justificativas,
        consequencia: impactoMax > 0
          ? `Risco de impacto de até ${fmtCompact(impactoMax)} nas próximas 6 semanas`
          : 'Risco de deterioração contínua sem dados suficientes para quantificar',
      };
    }

    case 'prazo_critico': {
      const startDate = contratoResumo?.start_date;
      const endDate = contratoResumo?.end_date;
      let custoDia = 0;
      if (startDate && endDate) {
        const durationDays = Math.max(1, Math.floor(
          (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000
        ));
        custoDia = valorGlobal / durationDays;
      }
      const diasAtraso = Number(ctx.dias_atraso) || 30;
      const baseImpact = custoDia * diasAtraso;
      const impactoMin = baseImpact * 0.7;
      const impactoMax = baseImpact * 1.3;

      const justificativas: string[] = [];
      if (diasAtraso > 0) justificativas.push(`Projeção de ${diasAtraso} dias de atraso`);
      if (custoDia > 0) justificativas.push(`Custo diário estimado: ${fmtCompact(custoDia)}/dia`);
      if (contratoResumo?.tendencia === 'piorando') justificativas.push('Tendência de execução piorando');
      if (justificativas.length === 0) justificativas.push('Prazo contratual sob pressão');

      return {
        acao: 'Avaliar aditivo de prazo com base em projeção de atraso — solicitar cronograma atualizado',
        prazo: 'Próximo ciclo (≤ 15 dias)',
        impactoMin,
        impactoMax,
        confianca: custoDia > 0 && signals >= 2 ? 'media' : 'baixa',
        severidade: 'critico',
        justificativas,
        consequencia: impactoMax > 0
          ? `Cada dia adicional de atraso custa ~${fmtCompact(custoDia)} — exposição acumulada de até ${fmtCompact(impactoMax)}`
          : 'Atraso pode gerar custos adicionais não quantificados',
      };
    }

    case 'desvio_progresso': {
      const deltaPct = Number(ctx.delta_percentual) || 0.05;
      const baseImpact = valorGlobal * Math.abs(deltaPct);
      const impactoMin = baseImpact * 0.8;
      const impactoMax = baseImpact * 1.2;

      const justificativas: string[] = [];
      justificativas.push(`Desvio físico-financeiro de ${(Math.abs(deltaPct) * 100).toFixed(1)}%`);
      if (contratoResumo?.tendencia === 'piorando') justificativas.push('Tendência de desvio crescente');
      if (probAtraso > 0.3) justificativas.push(`Probabilidade de atraso: ${(probAtraso * 100).toFixed(0)}%`);

      return {
        acao: 'Auditar medição — possível inconsistência físico-financeira entre execução e boletim',
        prazo: 'Até próximo boletim',
        impactoMin,
        impactoMax,
        confianca: 'media',
        severidade: 'atencao',
        justificativas,
        consequencia: `Desvio pode representar de ${fmtCompact(impactoMin)} a ${fmtCompact(impactoMax)} em sobrepreço ou subfaturamento`,
      };
    }

    case 'boletim_aprovado':
      return {
        acao: 'Nenhuma ação necessária — boletim aprovado conforme esperado',
        prazo: '—',
        impactoMin: 0,
        impactoMax: 0,
        confianca: 'alta',
        severidade: 'info',
        justificativas: ['Boletim aprovado dentro dos parâmetros esperados'],
        consequencia: 'Sem risco identificado',
      };

    default:
      return {
        acao: `Verificar alerta de tipo "${alerta.tipo}" — avaliação manual recomendada`,
        prazo: 'Sob demanda',
        impactoMin: 0,
        impactoMax: 0,
        confianca: 'baixa',
        severidade: 'info',
        justificativas: ['Tipo de alerta não mapeado automaticamente'],
        consequencia: 'Avaliar manualmente para determinar risco',
      };
  }
}

function fmtCompact(v: number): string {
  if (v >= 1e9) return `R$ ${(v / 1e9).toFixed(1).replace('.', ',')}B`;
  if (v >= 1e6) return `R$ ${(v / 1e6).toFixed(1).replace('.', ',')}M`;
  if (v >= 1e3) return `R$ ${(v / 1e3).toFixed(0)}K`;
  return `R$ ${v.toFixed(0)}`;
}
